import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { initiateGoogleAuth } from "../../core/auth/google/googleAuth";
import LoggedInCTAButtons from "../../core/components/LoggedInCTAButtons";
import { GoogleIcon, ArrowRightIcon } from "../../design-system/icons";
import { SecondaryButton } from "../../design-system";
import { APP_DOMAIN } from "../../core/utils/config";
import { logoFontStyle } from "../../design-system/brand/logoFont";
import { submitPotentialCustomerLead } from "../../core/utils/leadsApi";
import { apiFetch } from "../../core/utils/api";
import { demoPreviewUrl } from "../../core/utils/previewUrls";
import { buildReferralSearchParams, getReferralCoupon } from "../../core/utils/referralCoupon";
import { testimonialsData } from "./testimonialsData";
import { UserProfileIcon, StarIcon } from "../../design-system/icons";

import LandingNavbar from "./LandingNavbar";
import Footer from "./Footer";
import useGlobalAccessGrantActive from "./useGlobalAccessGrantActive";
import Website from "../../renderer/pages/Website";
import PreviewContext from "../../editor/context/PreviewContext";

import "./landing.css";

/** Append staged affiliate referral params when navigating to auth routes. */
const withReferralSearch = (path) => {
  const referral = getReferralCoupon();
  if (!referral?.code) return path;
  const params = buildReferralSearchParams(referral).replace(/^\?/, "");
  if (!params) return path;
  return path.includes("?") ? `${path}&${params}` : `${path}?${params}`;
};

const parseFeaturedWebsiteRoute = (route) => {
  const [username = "", domain = ""] = String(route || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

  return { username, domain };
};

const buildPreviewWebsite = (website, index) => {
  const { username, domain } = parseFeaturedWebsiteRoute(website?.url);
  if (!username || !domain) return null;

  return {
    ...website,
    previewKey: `${username}/${domain}-${index}`,
    username,
    domain,
    title: String(website?.name || "").trim() || `${username}/${domain}`,
  };
};

/** Matches `DEMO_PUBLIC_USERNAME` + demo specs in `website/utils/demo_public_sites.py`. */
const DEMO_SHOWCASE_USERNAME = "myvitae-showcase";

/**
 * When the featured endpoint fails (network / non-2xx), show demo previews so local dev
 * and transient outages still look reasonable. A successful response with an empty list
 * means the admin cleared the curated list — do not substitute demos in that case.
 */
const LANDING_FEATURED_FALLBACK_RAW = [
  { name: "Avery Chen", url: `${DEMO_SHOWCASE_USERNAME}/avery-chen` },
  { name: "Miles Carter", url: `${DEMO_SHOWCASE_USERNAME}/miles-carter` },
  { name: "Julian Rivera", url: `${DEMO_SHOWCASE_USERNAME}/julian-rivera` },
  { name: "Sofia Patel", url: `${DEMO_SHOWCASE_USERNAME}/sofia-patel` },
  { name: "Camila Rocha", url: `${DEMO_SHOWCASE_USERNAME}/camila-rocha` },
  { name: "Elliot Sage", url: `${DEMO_SHOWCASE_USERNAME}/elliot-sage` },
  { name: "Isabela Martins", url: `${DEMO_SHOWCASE_USERNAME}/isabela-martins` },
  { name: "Amina Hassan", url: `${DEMO_SHOWCASE_USERNAME}/amina-hassan` },
  { name: "Nathan Cole", url: `${DEMO_SHOWCASE_USERNAME}/nathan-cole` },
  { name: "Caroline Whitfield", url: `${DEMO_SHOWCASE_USERNAME}/caroline-whitfield` },
  { name: "Garrett Cole", url: `${DEMO_SHOWCASE_USERNAME}/garrett-cole` },
  { name: "Yuhan Zhao", url: `${DEMO_SHOWCASE_USERNAME}/yuhan-zhao` },
  { name: "Rafael Correia", url: `${DEMO_SHOWCASE_USERNAME}/rafael-correia` },
  { name: "Shen Yue", url: `${DEMO_SHOWCASE_USERNAME}/shen-yue` },
  { name: "Valentina Mendoza", url: `${DEMO_SHOWCASE_USERNAME}/valentina-mendoza` },
  { name: "Chris Millen", url: `${DEMO_SHOWCASE_USERNAME}/chris-millen` },
  { name: "Milla Leon", url: `${DEMO_SHOWCASE_USERNAME}/milla-leon` },
  { name: "Kenji Tanaka", url: `${DEMO_SHOWCASE_USERNAME}/kenji-tanaka` },
  { name: "Aditya Iyer", url: `${DEMO_SHOWCASE_USERNAME}/aditya-iyer` },
  { name: "Meera Krishnan", url: `${DEMO_SHOWCASE_USERNAME}/meera-krishnan` },
  { name: "Dr. Malik Johnson", url: `${DEMO_SHOWCASE_USERNAME}/malik-johnson` },
  { name: "Georgia Ellison", url: `${DEMO_SHOWCASE_USERNAME}/georgia-ellison` },
  { name: "Imani Adebayo", url: `${DEMO_SHOWCASE_USERNAME}/imani-adebayo` },
  { name: "Mateo Alvarez", url: `${DEMO_SHOWCASE_USERNAME}/mateo-alvarez` },
  { name: "Yassine Bennani", url: `${DEMO_SHOWCASE_USERNAME}/yassine-bennani` },
  { name: "Tomás Bianchi", url: `${DEMO_SHOWCASE_USERNAME}/tomas-bianchi` },
  { name: "Elise Vandenberghe", url: `${DEMO_SHOWCASE_USERNAME}/elise-vandenberghe` },
  { name: "Lucía Navarro", url: `${DEMO_SHOWCASE_USERNAME}/lucia-navarro` },
  { name: "Dr. Helena Morales", url: `${DEMO_SHOWCASE_USERNAME}/helena-morales` },
  { name: "Ji-yeon Park", url: `${DEMO_SHOWCASE_USERNAME}/ji-yeon-park` },
  { name: "Priya Venkatesh", url: `${DEMO_SHOWCASE_USERNAME}/priya-venkatesh` },
  { name: "Hannah Kowalski", url: `${DEMO_SHOWCASE_USERNAME}/hannah-kowalski` },
  { name: "Dr. Priya Nambiar", url: `${DEMO_SHOWCASE_USERNAME}/priya-nambiar` },
  { name: "Dr. Amara Osei", url: `${DEMO_SHOWCASE_USERNAME}/amara-osei` },
  { name: "Dr. Helena Vorbeck", url: `${DEMO_SHOWCASE_USERNAME}/helena-vorbeck` },
  { name: "Eleanor Park", url: `${DEMO_SHOWCASE_USERNAME}/eleanor-park` },
  { name: "Dr. Charlotte Kim", url: `${DEMO_SHOWCASE_USERNAME}/charlotte-kim` },
  { name: "Anika Deshmukh", url: `${DEMO_SHOWCASE_USERNAME}/anika-deshmukh` },
  { name: "Dr. Minh Tran", url: `${DEMO_SHOWCASE_USERNAME}/minh-tran` },
  { name: "Yu-Ting Lin", url: `${DEMO_SHOWCASE_USERNAME}/yu-ting-lin` },
];

const buildLandingFeaturedList = (raw) =>
  raw.map((w, i) => buildPreviewWebsite(w, i)).filter(Boolean);

const MIN_LANDING_SCROLL_DURATION_MOBILE_SECONDS = 40;
const MIN_LANDING_SCROLL_DURATION_DESKTOP_SECONDS = 30;
const MOBILE_SECONDS_PER_WEBSITE = 20;
const DESKTOP_SECONDS_PER_WEBSITE = 15;
const LANDING_SHOWCASE_MOBILE_BATCH_SIZE = 4;
const LANDING_SHOWCASE_ANIMATION_MS = 560;

const LandingPreviewLoadingSpinner = () => (
  <span className="landing-preview-loading-spinner" aria-hidden="true" />
);

const getLandingTrackDurations = (websiteCount) => ({
  mobile: Math.max(
    MIN_LANDING_SCROLL_DURATION_MOBILE_SECONDS,
    websiteCount * MOBILE_SECONDS_PER_WEBSITE
  ),
  desktop: Math.max(
    MIN_LANDING_SCROLL_DURATION_DESKTOP_SECONDS,
    websiteCount * DESKTOP_SECONDS_PER_WEBSITE
  ),
});

const LiveWebsitePreview = ({ website, scale, width = 1440, height = 1000 }) => {
  const previewContextValue = {
    mode: "responsive",
    viewport: null,
    containerWidth: width,
    contentWidth: width,
    onNavigate: () => {},
    showFrame: false,
    manualZoom: 1,
  };

  return (
    <div className="landing-iframe-wrapper">
      <PreviewContext.Provider value={previewContextValue}>
        <div className="landing-iframe-inner" style={{ width: `${width}px`, height: `${height}px`, transform: scale ? `scale(${scale})` : undefined, transformOrigin: "top left" }}>
          <Website username={website.username} domain={website.domain} subdomain="" />
        </div>
      </PreviewContext.Provider>
    </div>
  );
};

const WebsitePreview = ({ website, scale, width, height }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const staticSrc = demoPreviewUrl(website.domain);

  if (!imgFailed) {
    return (
      <div className="landing-iframe-wrapper">
        <img
          src={staticSrc}
          alt={website.title || website.domain}
          onError={() => setImgFailed(true)}
          className="w-full h-full object-cover object-top"
          loading="lazy"
          draggable={false}
        />
      </div>
    );
  }

  return <LiveWebsitePreview website={website} scale={scale} width={width} height={height} />;
};

const LandingWebsiteShowcase = ({
  websites = [],
  featuredLoadState = "ready",
  onOpenWebsite,
  onCreateOwn,
}) => {
  const [mobileVisibleCount, setMobileVisibleCount] = useState(LANDING_SHOWCASE_MOBILE_BATCH_SIZE);
  const mobileItems = [
    ...websites.map((website) => ({ type: "website", website })),
    { type: "create" },
  ];
  const visibleMobileItems = mobileItems.slice(0, mobileVisibleCount);
  const remainingMobileCount = Math.max(0, mobileItems.length - mobileVisibleCount);

  useEffect(() => {
    setMobileVisibleCount(LANDING_SHOWCASE_MOBILE_BATCH_SIZE);
  }, [websites.length]);

  const renderWebsiteCard = (website, keyPrefix = "showcase") => (
    <button
      key={`${keyPrefix}-${website.previewKey}`}
      type="button"
      className="landing-showcase-card group"
      onClick={() => onOpenWebsite(website.url)}
      aria-label={`Open ${website.title} website`}
    >
      <div className="landing-showcase-preview">
        <WebsitePreview website={website} />
      </div>
      <div className="landing-showcase-card-gradient" />
      <div className="landing-showcase-card-copy">
        <h3>{website.title}</h3>
        <p>{website.domain}</p>
      </div>
    </button>
  );

  const renderCreateOwnCard = (keyPrefix = "create-your-own") => (
    <button
      key={keyPrefix}
      type="button"
      className="landing-showcase-card landing-showcase-create-card"
      onClick={onCreateOwn}
      aria-label="Create your own website"
    >
      <div className="landing-showcase-create-orb">
        <ArrowRightIcon size="md" />
      </div>
      <div className="landing-showcase-create-copy">
        <h3>Create your own</h3>
        <p>Start from your CV and photo, then publish a professional site.</p>
      </div>
    </button>
  );

  return (
    <section className="landing-showcase-section" aria-labelledby="landing-showcase-title">
      <div className="landing-showcase-content">
        <div className="landing-showcase-heading">
          <p className="landing-showcase-eyebrow">Website inspiration</p>
          <h2 id="landing-showcase-title">
            Explore websites made with <span>{APP_DOMAIN}</span>
          </h2>
          <p>
            Browse the curated public sites selected for the landing page, then create your own when you are ready.
          </p>
        </div>

        {featuredLoadState === "loading" ? (
          <div className="landing-showcase-grid landing-showcase-grid--loading" aria-label="Loading website previews">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="landing-showcase-card landing-showcase-skeleton">
                <LandingPreviewLoadingSpinner />
              </div>
            ))}
          </div>
        ) : websites.length > 0 ? (
          <>
            <div className="landing-showcase-grid landing-showcase-grid--desktop" aria-label="Featured website previews">
              {websites.map((website) => renderWebsiteCard(website, "desktop"))}
              {renderCreateOwnCard("desktop-create-your-own")}
            </div>

            <div className="landing-showcase-mobile">
              <div className="landing-showcase-grid" aria-label="Featured website previews">
                {visibleMobileItems.map((item, index) =>
                  item.type === "create"
                    ? renderCreateOwnCard("mobile-create-your-own")
                    : renderWebsiteCard(item.website, `mobile-${index}`)
                )}
              </div>

              {remainingMobileCount > 0 ? (
                <div className="landing-showcase-more-wrap">
                  <button
                    type="button"
                    className="landing-showcase-more-button"
                    onClick={() =>
                      setMobileVisibleCount((count) =>
                        Math.min(count + LANDING_SHOWCASE_MOBILE_BATCH_SIZE, mobileItems.length)
                      )
                    }
                  >
                    View more websites
                  </button>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="landing-showcase-empty">
            <p>No featured websites are selected yet.</p>
            {renderCreateOwnCard("empty-create-your-own")}
          </div>
        )}
      </div>
    </section>
  );
};

const TYPEWRITER_PHRASES = [
  "Get hired faster by top companies.",
  "Impress MBA and Masters admissions.",
  "Showcase your academic work."
];

const TypewriterEffect = ({ centered = false }) => {
  const [text, setText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(80);

  useEffect(() => {
    let timer;
    const handleType = () => {
      const i = loopNum % TYPEWRITER_PHRASES.length;
    const fullText = TYPEWRITER_PHRASES[i];

      setText(isDeleting 
        ? fullText.substring(0, text.length - 1) 
        : fullText.substring(0, text.length + 1)
      );

      setTypingSpeed(isDeleting ? 30 : 70);

      if (!isDeleting && text === fullText) {
        timer = setTimeout(() => setIsDeleting(true), 2500);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
        timer = setTimeout(() => {}, 500);
      } else {
        timer = setTimeout(handleType, typingSpeed);
      }
    };

    timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed]);

  return (
    <div
      className={`flex items-center font-heading text-2xl sm:text-3xl font-bold h-12 ${
        centered ? "justify-center" : "justify-center lg:justify-start"
      }`}
    >
      <span className="mr-3 text-blue-500 text-3xl">❯</span>
      <span className="bg-gradient-to-r from-slate-700 to-slate-500 dark:from-dark-200 dark:to-dark-400 text-transparent bg-clip-text">
        {text}
      </span>
      <span className="animate-pulse border-r-2 border-blue-500 ml-1 h-10"></span>
    </div>
  );
};

const LandingHero = ({ isFreeGrantActive }) => {
  const [email, setEmail] = useState("");
  const [showWebsiteShowcase, setShowWebsiteShowcase] = useState(false);
  const [shouldRenderWebsiteShowcase, setShouldRenderWebsiteShowcase] = useState(false);
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const showcaseRef = useRef(null);
  const showcaseAnimationTimeoutRef = useRef(null);
  const landingLeadSituation = "LANDING_EMAIL";
  
  const [websites, setWebsites] = useState([]);
  const [featuredLoadState, setFeaturedLoadState] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    const applyWebsites = (list) => {
      if (cancelled) return;
      setWebsites(list);
      setFeaturedLoadState("ready");
    };

    const fetchPublicWebsites = async () => {
      setFeaturedLoadState("loading");
      try {
        const response = await apiFetch("/api/websites/public/featured/");
        if (!response.ok) {
          applyWebsites(buildLandingFeaturedList(LANDING_FEATURED_FALLBACK_RAW));
          return;
        }
        const data = await response.json();
        const parsed = buildLandingFeaturedList(Array.isArray(data) ? data : []);
        applyWebsites(parsed);
      } catch (err) {
        console.warn("Failed to fetch featured public websites", err);
        applyWebsites(buildLandingFeaturedList(LANDING_FEATURED_FALLBACK_RAW));
      }
    };
    fetchPublicWebsites();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (showcaseAnimationTimeoutRef.current) {
        clearTimeout(showcaseAnimationTimeoutRef.current);
      }
    };
  }, []);

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = withReferralSearch(`/edit/register?email=${encodeURIComponent(trimmedEmail)}`);
      navigate(base);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const handleWebsiteClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCreateOwnWebsite = () => {
    if (auth?.userId && auth?.username) {
      navigate(`/dashboard/${auth.username}`);
      return;
    }
    navigate(withReferralSearch("/edit/register"));
  };

  const handleRevealWebsiteShowcase = () => {
    if (showcaseAnimationTimeoutRef.current) {
      clearTimeout(showcaseAnimationTimeoutRef.current);
      showcaseAnimationTimeoutRef.current = null;
    }

    if (showWebsiteShowcase) {
      setShowWebsiteShowcase(false);
      showcaseAnimationTimeoutRef.current = setTimeout(() => {
        setShouldRenderWebsiteShowcase(false);
        showcaseAnimationTimeoutRef.current = null;
      }, LANDING_SHOWCASE_ANIMATION_MS);
      return;
    }

    setShouldRenderWebsiteShowcase(true);
    setShowWebsiteShowcase(true);
    window.requestAnimationFrame(() => {
      showcaseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  // Duplicate websites to create a seamless infinite scroll effect
  const scrollItems = [...websites, ...websites];
  const scrollTrackDurations = getLandingTrackDurations(websites.length);

  const showHeroPreviewColumn = featuredLoadState === "loading" || websites.length > 0;

  return (
    <div className="landing-container relative">
      {/* Aurora Background */}
      <div className="landing-aurora-bg">
        <div className="landing-aurora-blob landing-blob-1"></div>
        <div className="landing-aurora-blob landing-blob-2"></div>
        <div className="landing-aurora-blob landing-blob-3"></div>
        {/* Fade out gradient at the bottom to transition smoothly into the next section */}
        <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-[#f8fafc] dark:from-dark-950 to-transparent z-10"></div>
      </div>

      <LandingNavbar />
      
      <div
        className={`landing-hero-section${!showHeroPreviewColumn ? " landing-hero-section--no-previews" : ""}`}
      >
        
        {/* Left Side: Text and Glassmorphism CTA */}
        <div
          className={`landing-text-content${!showHeroPreviewColumn ? " landing-text-content--no-previews" : ""}`}
        >
          <div className="mb-4">
            <h1 className="text-6xl sm:text-7xl lg:text-8xl bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400 text-transparent bg-clip-text pb-2" style={logoFontStyle}>
              {APP_DOMAIN}
            </h1>
          </div>
          
          <h2
            className={`text-base sm:text-lg lg:text-xl mb-6 leading-snug text-slate-800 dark:text-dark-200 transition-colors duration-300 ${
              showHeroPreviewColumn ? "lg:whitespace-nowrap" : "lg:whitespace-normal"
            }`}
          >
            Create a personal website that makes you <span className="italic font-semibold">impossible to ignore</span>
            {isFreeGrantActive ? (
              <span className="text-green-400 font-extrabold ml-1">{" — for free"}</span>
            ) : null}
          </h2>

          <p className="mb-6 font-body text-base sm:text-lg lg:text-xl font-bold leading-snug text-slate-700 dark:text-dark-200">
            A product of Vitae AI LLC
          </p>

          <div className="mb-10 transition-colors duration-300">
            <TypewriterEffect centered={!showHeroPreviewColumn} />
          </div>

          <div className="landing-glass-card">
            {!isHydrated ? (
              <div className="h-32" />
            ) : auth && auth.userId ? (
              <LoggedInCTAButtons username={auth.username} />
            ) : (
              <>
                <div className="mb-5">
                  <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-1">Claim your custom domain</h3>
                  <p className="text-sm text-slate-500 dark:text-dark-400">Join thousands of professionals standing out online.</p>
                </div>
                <form
                  className="landing-input-wrapper"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isEmailValid) return;
                    handleCreateWebsite();
                  }}
                >
                  <input
                    type="email"
                    aria-label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email to start"
                    className="landing-input"
                  />
                  <button
                    type="submit"
                    disabled={!isEmailValid}
                    className="landing-submit-btn"
                    aria-label="Get started"
                  >
                    <ArrowRightIcon size="sm" />
                  </button>
                </form>

                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
                  <span className="px-4 text-sm text-slate-400 dark:text-dark-500 uppercase tracking-widest">or</span>
                  <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
                </div>

                <SecondaryButton
                  type="button"
                  onClick={handleSocialLogin}
                  fullWidth={true}
                  leftIcon={<GoogleIcon size="sm" />}
                  variant="alwaysLight"
                  className="bg-white/50 dark:bg-white border-white/50 dark:border-slate-200 text-slate-900 dark:text-slate-900 hover:bg-white dark:hover:bg-slate-100 backdrop-blur-sm transition-all"
                >
                  Continue with Google
                </SecondaryButton>
              </>
            )}
          </div>
        </div>

        {/* Right Side: Isometric 3D Scroll */}
        {showHeroPreviewColumn ? (
          <div className="landing-visual-content">
            <div className="landing-isometric-scene">
              <div
                className="landing-isometric-track"
                style={{
                  "--landing-scroll-duration-mobile": `${scrollTrackDurations.mobile}s`,
                  "--landing-scroll-duration-desktop": `${scrollTrackDurations.desktop}s`,
                }}
              >
                {featuredLoadState === "loading" ? (
                  [1, 2, 3, 4].map((i) => (
                    <div key={i} className="landing-preview-card flex items-center justify-center bg-slate-100 dark:bg-dark-800">
                      <LandingPreviewLoadingSpinner />
                    </div>
                  ))
                ) : scrollItems.length > 0 ? (
                  scrollItems.map((website, i) => (
                    <div
                      key={`${website.previewKey}-${i}`}
                      className="landing-preview-card"
                      onClick={() => handleWebsiteClick(website.url)}
                    >
                      <WebsitePreview website={website} />
                      <div className="landing-card-overlay">
                        <h3 className="landing-card-title">{website.title}</h3>
                        <p className="landing-card-domain">{website.domain}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="landing-preview-card flex items-center justify-center bg-slate-100 dark:bg-dark-800">
                    <span className="text-slate-400 dark:text-dark-500 text-sm px-4 text-center">
                      No featured websites to preview yet.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className="landing-showcase-reveal">
          <button
            type="button"
            className="landing-showcase-reveal-button"
            onClick={handleRevealWebsiteShowcase}
            aria-expanded={showWebsiteShowcase}
            aria-label={showWebsiteShowcase ? "Hide website previews" : "View more websites"}
          >
            {showWebsiteShowcase ? (
              <svg
                aria-hidden="true"
                className="landing-showcase-reveal-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 15l6-6 6 6"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              "View more websites"
            )}
          </button>
        </div>

      </div>

      <div className="landing-after-hero relative z-10 transition-colors duration-300">
        <div
          ref={showcaseRef}
          className={`landing-showcase-collapse${showWebsiteShowcase ? " landing-showcase-collapse--open" : ""}`}
          aria-hidden={!showWebsiteShowcase}
          inert={showWebsiteShowcase ? undefined : true}
        >
          <div className="landing-showcase-collapse-inner">
            {shouldRenderWebsiteShowcase ? (
              <LandingWebsiteShowcase
                websites={websites}
                featuredLoadState={featuredLoadState}
                onOpenWebsite={handleWebsiteClick}
                onCreateOwn={handleCreateOwnWebsite}
              />
            ) : null}
          </div>
        </div>

        <CreationSteps />
        <Testimonials />
        <FinalCTA
          isFreeGrantActive={isFreeGrantActive}
          websites={websites}
          featuredLoadState={featuredLoadState}
        />

        {!showWebsiteShowcase ? (
          <LandingWebsiteShowcase
            websites={websites}
            featuredLoadState={featuredLoadState}
            onOpenWebsite={handleWebsiteClick}
            onCreateOwn={handleCreateOwnWebsite}
          />
        ) : null}

        <Footer />
      </div>
    </div>
  );
};

const CreationSteps = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    {
      id: 1,
      title: "Upload your CV & Photo",
      color: "from-blue-700 to-blue-600",
    },
    {
      id: 2,
      title: "Choose Your Style",
      color: "from-blue-500 to-sky-500",
    },
    {
      id: 3,
      title: "Wait for the AI Magic",
      color: "from-sky-400 to-cyan-300",
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0 sm:py-8 relative">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white pb-2">
          Creation <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400 text-transparent bg-clip-text">Reimagined</span>
        </h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Column: Steps */}
        <div className="flex flex-col gap-6">
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            return (
              <div
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-500 overflow-hidden ${
                  isActive 
                    ? 'bg-white dark:bg-dark-800/80 shadow-2xl border border-slate-200 dark:border-dark-700 transform lg:scale-105 z-10 block' 
                    : 'hover:bg-white/50 dark:hover:bg-dark-800/50 border border-transparent opacity-70 hover:opacity-100 hidden lg:block'
                }`}
              >
                {/* Progress Bar */}
                {isActive && (
                  <div 
                    key={`progress-${activeStep}`} 
                    className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${step.color} animate-[progress_5s_linear_forwards]`}
                  ></div>
                )}

                <div className="flex items-center gap-5">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-xl font-black transition-all duration-500 ${
                    isActive 
                      ? `bg-gradient-to-br ${step.color} text-white shadow-lg scale-110` 
                      : 'bg-slate-100 dark:bg-dark-800 text-slate-400 dark:text-dark-500'
                  }`}>
                    {step.id}
                  </div>
                  <h3 className={`font-heading text-2xl font-bold transition-colors duration-500 ${
                    isActive ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-dark-400'
                  }`}>
                    {step.title}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Column: Interactive Stage */}
        <div className="relative w-full h-[350px] md:h-[450px] rounded-[2rem] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-dark-800 dark:to-dark-900 border border-slate-200 dark:border-dark-700 overflow-hidden shadow-2xl flex items-center justify-center">
          
          {/* Background Ambient Gradients */}
          <div className="absolute inset-0 opacity-30 dark:opacity-20">
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-700 to-transparent transition-opacity duration-1000 ${activeStep === 0 ? 'opacity-100' : 'opacity-0'}`}></div>
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500 to-transparent transition-opacity duration-1000 ${activeStep === 1 ? 'opacity-100' : 'opacity-0'}`}></div>
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br from-sky-400 to-transparent transition-opacity duration-1000 ${activeStep === 2 ? 'opacity-100' : 'opacity-0'}`}></div>
          </div>

          {/* Stage 1: Upload & Extract */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 transform ${activeStep === 0 ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0 pointer-events-none'}`}>
            {/* Dashed Dropzone */}
            <div className="absolute inset-8 border-2 border-dashed border-blue-600/30 rounded-3xl animate-[pulse_4s_ease-in-out_infinite]"></div>
            
            {/* AI Analysis Badge */}
            <div className="absolute top-12 left-1/2 -translate-x-1/2 bg-dark-900/80 dark:bg-dark-50/90 backdrop-blur-md px-4 py-1.5 rounded-full shadow-xl border border-slate-700 dark:border-dark-200 z-40 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-600 animate-ping"></div>
              <span className="text-xs font-bold tracking-widest uppercase text-white dark:text-dark-900">AI Analysis</span>
            </div>

            <div className="relative flex items-center justify-center w-full h-full">
              {/* CV Card */}
              <div className="absolute w-40 h-56 bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-slate-200 dark:border-dark-700 p-4 overflow-hidden animate-[float_3s_ease-in-out_infinite] z-10 -ml-16">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4"></div>
                <div className="w-full h-2 bg-slate-100 dark:bg-dark-700 rounded mb-3"></div>
                <div className="w-5/6 h-2 bg-slate-100 dark:bg-dark-700 rounded mb-3"></div>
                <div className="w-4/6 h-2 bg-slate-100 dark:bg-dark-700 rounded mb-6"></div>
                <div className="w-full h-2 bg-slate-100 dark:bg-dark-700 rounded mb-3"></div>
                <div className="w-3/4 h-2 bg-slate-100 dark:bg-dark-700 rounded"></div>
              </div>

              {/* Image Card */}
              <div className="absolute w-32 h-32 bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-slate-200 dark:border-dark-700 p-2 overflow-hidden animate-[float_3.5s_ease-in-out_infinite] z-20 ml-20 mt-16 flex items-center justify-center">
                <div className="w-full h-full bg-slate-100 dark:bg-dark-700 rounded-lg flex items-center justify-center">
                  <svg
                    aria-hidden="true"
                    className="w-12 h-12 text-slate-300 dark:text-dark-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="2.5"
                      y="5"
                      width="19"
                      height="14"
                      rx="2.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <circle cx="8" cy="10" r="1.5" fill="currentColor" />
                    <path
                      d="M5.5 17l4-4a1 1 0 0 1 1.4 0l2 2 2.6-2.6a1 1 0 0 1 1.4 0L19 14.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Extracted Data Tags (Animated) */}
              <div className="absolute z-30 flex flex-col gap-2 -ml-32 mt-10">
                <div className="bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 animate-[extract_4s_ease-in-out_infinite]">Software Engineer</div>
                <div className="bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 animate-[extract_4s_ease-in-out_infinite_0.5s]">Harvard University</div>
                <div className="bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 animate-[extract_4s_ease-in-out_infinite_1s]">5+ Years Exp</div>
              </div>

              {/* Scanner Line */}
              <div className="absolute w-72 h-1 bg-blue-600 shadow-[0_0_20px_rgba(37,99,235,1)] animate-[scan_3s_ease-in-out_infinite] z-30"></div>
            </div>
          </div>

          {/* Stage 2: Customize */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 transform ${activeStep === 1 ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0 pointer-events-none'}`}>
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Palette Card */}
              <div className="absolute top-1/4 left-[10%] sm:left-[15%] w-32 bg-white/90 dark:bg-dark-800/90 backdrop-blur rounded-2xl shadow-xl border border-slate-200 dark:border-dark-700 p-4 animate-[float_3s_ease-in-out_infinite]">
                <div className="text-xs font-bold text-slate-500 mb-3">Colors</div>
                <div className="flex justify-between relative">
                  <div className="w-6 h-6 rounded-full bg-blue-700 shadow-lg relative">
                    <div className="absolute -inset-1 rounded-full border-2 border-blue-700 animate-[fade-ring-1_6s_infinite]"></div>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-blue-500 shadow-lg relative">
                    <div className="absolute -inset-1 rounded-full border-2 border-blue-500 animate-[fade-ring-2_6s_infinite]"></div>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-sky-400 shadow-lg relative">
                    <div className="absolute -inset-1 rounded-full border-2 border-sky-400 animate-[fade-ring-3_6s_infinite]"></div>
                  </div>
                </div>
              </div>
              
              {/* Typography Card */}
              <div className="absolute bottom-1/4 right-[10%] sm:right-[15%] w-32 bg-white/90 dark:bg-dark-800/90 backdrop-blur rounded-2xl shadow-xl border border-slate-200 dark:border-dark-700 p-4 animate-[float_4s_ease-in-out_infinite_reverse]">
                <div className="text-xs font-bold text-slate-500 mb-2">Typography</div>
                <div className="text-3xl font-serif font-bold text-slate-800 dark:text-white animate-[font-change_4s_infinite]">Aa</div>
              </div>

              {/* Tone Card */}
              <div className="absolute top-1/3 right-[5%] sm:right-[10%] bg-white/90 dark:bg-dark-800/90 backdrop-blur rounded-xl shadow-xl border border-slate-200 dark:border-dark-700 px-4 py-2 animate-[float_3.5s_ease-in-out_infinite]">
                <div className="text-xs font-bold text-blue-500">Tone: Professional</div>
              </div>
              
              {/* Central Wireframe changing */}
              <div className="w-48 h-48 bg-white dark:bg-dark-800 rounded-[2rem] shadow-2xl border-4 border-blue-500 p-4 flex flex-col gap-3 animate-[border-color-change_6s_infinite]">
                <div className="w-full h-1/2 rounded-xl animate-[bg-color-change_6s_infinite]"></div>
                <div className="w-3/4 h-4 bg-slate-100 dark:bg-dark-700 rounded-full"></div>
                <div className="w-1/2 h-4 bg-slate-100 dark:bg-dark-700 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Stage 3: Magic */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 transform ${activeStep === 2 ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0 pointer-events-none'}`}>
            
            {/* Generating Overlay Animation */}
            {activeStep === 2 && (
              <div className="absolute inset-0 bg-dark-900/40 backdrop-blur-md z-50 flex flex-col items-center justify-center animate-[fade-out-overlay_1.5s_ease-in-out_forwards] rounded-[2rem]">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <div className="text-white font-bold tracking-widest uppercase text-sm animate-pulse">Generating AI...</div>
              </div>
            )}

            {/* Mini Browser */}
            <div className="relative w-64 h-80 bg-white dark:bg-dark-900 rounded-xl shadow-2xl border border-slate-200 dark:border-dark-700 overflow-hidden flex flex-col animate-[float_4s_ease-in-out_infinite]">
              {/* Browser header */}
              <div className="h-8 bg-slate-100 dark:bg-dark-800 flex items-center px-3 gap-1.5 border-b border-slate-200 dark:border-dark-700">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                <div className="ml-4 flex-1 h-4 bg-white dark:bg-dark-900 rounded text-[8px] text-center text-slate-400 flex items-center justify-center font-mono">myvitae.ai/you</div>
              </div>
              {/* Website content */}
              <div className="p-4 flex-1 flex flex-col gap-3 relative">
                <div className="w-full h-24 bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400 rounded-lg shadow-inner flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white">
                    <UserProfileIcon size="lg" />
                  </div>
                </div>
                <div className="w-2/3 h-4 bg-slate-200 dark:bg-dark-700 rounded"></div>
                <div className="w-full h-2 bg-slate-200 dark:bg-dark-700 rounded"></div>
                <div className="w-5/6 h-2 bg-slate-200 dark:bg-dark-700 rounded mb-2"></div>
                
                {/* Message Me Button */}
                <div className="w-24 h-6 bg-blue-500 rounded-full flex items-center justify-center text-[8px] font-bold text-white shadow-md animate-[button-click_5s_ease-in-out_forwards]" id="mockup-btn">
                  Message Me
                </div>
                
                {/* Success Overlay */}
                <div key={`success-${activeStep}`} className="absolute inset-0 bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm flex items-center justify-center opacity-0 animate-[fade-in_0.5s_ease-out_3s_forwards] pointer-events-none">
                  <div className="bg-green-500 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2 transform scale-0 animate-[pop-in_0.5s_ease-out_3s_forwards]">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    Live
                  </div>
                </div>
              </div>
            </div>

            {/* Animated Cursor */}
            {activeStep === 2 && (
              <div className="absolute z-40 w-6 h-6 text-slate-800 dark:text-white animate-[cursor-move_5s_ease-in-out_forwards]" style={{ filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.3))', left: 'calc(50% + 20px)', top: 'calc(50% + 10px)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
                  <path d="M7 2l12 11.2-5.8.5 3.3 7.3-2.2.9-3.2-7.4-4.4 4.7z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes scan {
          0%, 100% { top: 15%; opacity: 0; }
          10%, 90% { opacity: 1; }
          50% { top: 85%; }
        }
        @keyframes extract {
          0% { transform: translateX(-20px); opacity: 0; }
          20%, 80% { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(20px); opacity: 0; }
        }
        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); box-shadow: 0 0 15px rgba(0,0,0,0.2); }
        }
        @keyframes font-change {
          0%, 100% { font-family: serif; }
          33% { font-family: sans-serif; font-style: italic; }
          66% { font-family: monospace; }
        }
        @keyframes bg-color-change {
          0%, 100% { background-color: rgba(29, 78, 216, 0.2); } /* blue-700 */
          33% { background-color: rgba(59, 130, 246, 0.2); } /* blue-500 */
          66% { background-color: rgba(56, 189, 248, 0.2); } /* sky-400 */
        }
        @keyframes border-color-change {
          0%, 100% { border-color: rgba(29, 78, 216, 0.8); } /* blue-700 */
          33% { border-color: rgba(59, 130, 246, 0.8); } /* blue-500 */
          66% { border-color: rgba(56, 189, 248, 0.8); } /* sky-400 */
        }
        @keyframes text-color-change {
          0%, 100% { color: rgb(236, 72, 153); } /* pink */
          33% { color: rgb(168, 85, 247); } /* purple */
          66% { color: rgb(59, 130, 246); } /* blue */
        }
        @keyframes fade-ring-1 {
          0%, 25%, 100% { opacity: 1; transform: scale(1); }
          33%, 90% { opacity: 0; transform: scale(0.8); }
        }
        @keyframes fade-ring-2 {
          0%, 25%, 66%, 100% { opacity: 0; transform: scale(0.8); }
          33%, 58% { opacity: 1; transform: scale(1); }
        }
        @keyframes fade-ring-3 {
          0%, 58%, 100% { opacity: 0; transform: scale(0.8); }
          66%, 90% { opacity: 1; transform: scale(1); }
        }
        @keyframes cursor-move {
          0% { transform: translate(100px, 100px); opacity: 0; }
          10% { opacity: 1; }
          40% { transform: translate(-45px, 60px); }
          50% { transform: translate(-45px, 60px) scale(0.9); } /* Click down */
          60% { transform: translate(-45px, 60px) scale(1); } /* Click up */
          90% { opacity: 1; }
          100% { transform: translate(100px, 100px); opacity: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes fade-in {
          to { opacity: 1; }
        }
        @keyframes pop-in {
          to { transform: scale(1); }
        }
        @keyframes fade-out-overlay {
          0%, 60% { opacity: 1; visibility: visible; }
          100% { opacity: 0; visibility: hidden; }
        }
        @keyframes button-click {
          0%, 45% { transform: scale(1); background-color: #3b82f6; }
          50% { transform: scale(1.1); background-color: #60a5fa; box-shadow: 0 0 15px rgba(59, 130, 246, 0.5); }
          55%, 100% { transform: scale(1); background-color: #3b82f6; }
        }
        @keyframes marquee-horizontal {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}} />
    </div>
  );
};

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isPaused) {
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
      }, 5000);
    }
    return () => clearInterval(timerRef.current);
  }, [isPaused]);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon key={index} size="sm" className="text-yellow-400" filled />
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white pb-2">
          What Our <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400 text-transparent bg-clip-text">Users Say</span>
        </h2>
        <p className="text-lg sm:text-xl text-slate-600 dark:text-dark-300 mt-2">
          Join thousands of professionals who trust {APP_DOMAIN}
        </p>
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonialsData.map((testimonial) => (
          <div 
            key={testimonial.id}
            className="bg-white/50 dark:bg-dark-800/50 backdrop-blur-md border border-slate-200 dark:border-dark-700 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-sky-100 dark:from-dark-700 dark:to-dark-600 rounded-full flex items-center justify-center mr-4 shadow-inner">
                <UserProfileIcon size="lg" className="text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">
                  {testimonial.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-dark-400">
                  {testimonial.profession}
                </p>
              </div>
            </div>

            <div className="flex items-center mb-4">
              {renderStars(testimonial.rating)}
            </div>

            <p className="text-slate-700 dark:text-dark-300 leading-relaxed">
              "{testimonial.feedback}"
            </p>
          </div>
        ))}
      </div>

      {/* Mobile Carousel View */}
      <div 
        className="block md:hidden relative px-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        <div className="overflow-hidden rounded-2xl pb-4">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {testimonialsData.map((testimonial, index) => {
              const isActive = index === activeIndex;
              return (
                <div key={`mobile-${testimonial.id}`} className="w-full flex-shrink-0">
                  <div className="bg-white/50 dark:bg-dark-800/50 backdrop-blur-md border border-slate-200 dark:border-dark-700 rounded-2xl p-6 shadow-xl relative overflow-hidden h-full">
                    
                    {/* Progress Bar */}
                    {isActive && (
                      <div 
                        key={`progress-test-${activeIndex}`} 
                        className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-sky-400 animate-[progress_5s_linear_forwards]"
                        style={{ animationPlayState: isPaused ? 'paused' : 'running' }}
                      ></div>
                    )}

                    <div className="flex items-center mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-sky-100 dark:from-dark-700 dark:to-dark-600 rounded-full flex items-center justify-center mr-4 shadow-inner">
                        <UserProfileIcon size="lg" className="text-blue-500 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">
                          {testimonial.name}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-dark-400">
                          {testimonial.profession}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center mb-4">
                      {renderStars(testimonial.rating)}
                    </div>

                    <p className="text-slate-700 dark:text-dark-300 leading-relaxed text-sm">
                      "{testimonial.feedback}"
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation Arrows */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-0 pointer-events-none">
          <button 
            onClick={(e) => { e.preventDefault(); prevTestimonial(); }}
            className="w-10 h-10 rounded-full bg-white/90 dark:bg-dark-800/90 backdrop-blur shadow-lg border border-slate-200 dark:border-dark-700 flex items-center justify-center text-slate-800 dark:text-white pointer-events-auto transform -translate-x-2 active:scale-95 transition-transform"
            aria-label="Previous testimonial"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button 
            onClick={(e) => { e.preventDefault(); nextTestimonial(); }}
            className="w-10 h-10 rounded-full bg-white/90 dark:bg-dark-800/90 backdrop-blur shadow-lg border border-slate-200 dark:border-dark-700 flex items-center justify-center text-slate-800 dark:text-white pointer-events-auto transform translate-x-2 active:scale-95 transition-transform"
            aria-label="Next testimonial"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const FinalCTA = ({ isFreeGrantActive, websites = [], featuredLoadState = "ready" }) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const landingLeadSituation = "LANDING_EMAIL";

  const marqueeItems = [...websites, ...websites];
  const marqueeTrackDurations = getLandingTrackDurations(websites.length);

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = withReferralSearch(`/edit/register?email=${encodeURIComponent(trimmedEmail)}`);
      navigate(base);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const handleWebsiteClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  const showPreviewColumn = featuredLoadState === "loading" || websites.length > 0;

  return (
    <div className={`landing-cta-section${!showPreviewColumn ? " landing-cta-section--no-previews" : ""}`}>
      {/* Left Side: Isometric 3D Scroll (Mirrored) */}
      {showPreviewColumn ? (
        <div className="landing-visual-content landing-visual-left">
          <div className="landing-isometric-scene landing-isometric-scene-left">
            <div
              className="landing-isometric-track landing-isometric-track-up"
              style={{
                "--landing-scroll-duration-mobile": `${marqueeTrackDurations.mobile}s`,
                "--landing-scroll-duration-desktop": `${marqueeTrackDurations.desktop}s`,
              }}
            >
              {featuredLoadState === "loading" ? (
                [1, 2, 3, 4].map((i) => (
                  <div key={i} className="landing-preview-card flex items-center justify-center bg-slate-100 dark:bg-dark-800">
                    <LandingPreviewLoadingSpinner />
                  </div>
                ))
              ) : marqueeItems.length > 0 ? (
                marqueeItems.map((website, i) => (
                  <div
                    key={`cta-preview-${website.previewKey}-${i}`}
                    className="landing-preview-card"
                    onClick={() => handleWebsiteClick(website.url)}
                  >
                    <WebsitePreview website={website} />
                    <div className="landing-card-overlay">
                      <h3 className="landing-card-title">{website.title}</h3>
                      <p className="landing-card-domain">{website.domain}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="landing-preview-card flex items-center justify-center bg-slate-100 dark:bg-dark-800">
                  <span className="text-slate-400 dark:text-dark-500 text-sm px-4 text-center">
                    No featured websites to preview yet.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Right Side: Text and CTA */}
      <div className={`landing-cta-text${!showPreviewColumn ? " landing-cta-text--no-previews" : ""}`}>
        <div className="mb-4">
          <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 dark:text-white pb-2 mb-4 leading-tight">
            Ready to {showPreviewColumn ? <br className="hidden lg:block" /> : " "}
            <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400 text-transparent bg-clip-text">stand out?</span>
          </h2>
        </div>
        <h2 className="text-base sm:text-lg lg:text-xl mb-8 leading-snug text-slate-800 dark:text-dark-200 transition-colors duration-300">
          Create your professional website today
          {isFreeGrantActive ? (
            <span className="text-green-400 font-extrabold ml-1">{" — for free"}</span>
          ) : null}.
        </h2>

        <div className="landing-glass-card mt-0">
          {!isHydrated ? (
            <div className="h-32" />
          ) : auth && auth.userId ? (
            <LoggedInCTAButtons username={auth.username} />
          ) : (
            <>
              <div className="mb-5">
                <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-1">Claim your custom domain</h3>
                <p className="text-sm text-slate-500 dark:text-dark-400">Join thousands of professionals standing out online.</p>
              </div>
              <form
                className="landing-input-wrapper"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isEmailValid) return;
                  handleCreateWebsite();
                }}
              >
                <input
                  type="email"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email to start"
                  className="landing-input"
                />
                <button
                  type="submit"
                  disabled={!isEmailValid}
                  className="landing-submit-btn"
                  aria-label="Get started"
                >
                  <ArrowRightIcon size="sm" />
                </button>
              </form>

              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
                <span className="px-4 text-sm text-slate-400 dark:text-dark-500 uppercase tracking-widest">or</span>
                <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
              </div>

              <SecondaryButton
                type="button"
                onClick={handleSocialLogin}
                fullWidth={true}
                leftIcon={<GoogleIcon size="sm" />}
                variant="alwaysLight"
                className="bg-white/50 dark:bg-white border-white/50 dark:border-slate-200 text-slate-900 dark:text-slate-900 hover:bg-white dark:hover:bg-slate-100 backdrop-blur-sm transition-all"
              >
                Continue with Google
              </SecondaryButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const isFreeGrantActive = useGlobalAccessGrantActive();

  return <LandingHero isFreeGrantActive={isFreeGrantActive} />;
};

export default Landing;
